<template>
  <div class="p-4 bg-white rounded-lg">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Email Invoice - {{ invoiceNumber }}</h2>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Address</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="recipientEmail" type="email"
             placeholder="Enter recipient email address"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Subject</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="emailSubject" type="text"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Text</label>
      <StyledTextEditor v-model="emailMessage" :options="editorOptions"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Send Copy To (Sent as separate email)</label>
      <Chips v-model="ccEmail" class="w-full" separator=","/>
    </div>

    <div class="mb-4 flex items-center">
      <Checkbox v-model="hasAttachments" :binary="true" class="mr-2"/>
      <label class="block uppercase font-semibold text-sm">Include 'Invoice' Attachment</label>
    </div>

    <div class="flex justify-end space-x-2">
      <AppButton color="gray" @click="setAsDefault" label="Set as Default"></AppButton>
      <AppButton color="blue" @click="sendInvoice" label="Send Invoice">
        <template v-slot:icon>
          <Icon iconType="send"/>
        </template>
      </AppButton>
    </div>

    <div class="mt-10">
      <EmailQueueTable ref="emailQueueTable" :referenceId="this.invoiceId"></EmailQueueTable>
    </div>
  </div>
</template>

<script>
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import StyledTextEditor from "@/components/StyledTextEditor.vue";
import notificationMixin from "@/mixins/notificationMixin";
import EmailQueueTable from "@/views/HR/Sales/Components/EmailQueueTable.vue";

export default {
  name: "EmailSendInvoice",
  mixins: [notificationMixin],
  components: {
    Icon,
    AppButton,
    EmailQueueTable,
    StyledTextEditor
  },
  props: {
    invoiceId: {
      type: String,
      required: true,
    },
    invoiceNumber: {
      type: String,
      required: true,
    },
    customer: {
      type: Object,
      required: true,
    },
    invoice: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      recipientEmail: '',
      emailSubject: '',
      emailMessage: '',
      ccEmail: null,
      defaultSubject: '',
      defaultEmailText: '',
      hasAttachments: true,
      attachmentUrls: '',
      editorOptions: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{'list': 'ordered'}, {'list': 'bullet'}],
        ]
      },
    };
  },
  computed: {
    // A contact with no default person has no `people` at all, so everything below has to cope
    // with that and leave the recipient to be typed in.
    person() {
      return (this.customer && this.customer.people) || {};
    },
  },
  mounted() {
    this.fetchEmailTemplate();
    this.recipientEmail = this.person.email || '';
  },
  methods: {
    formatMoney(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '£0.00';
      }
      return `£${parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    },
    replacePlaceholders(template, data) {
      return template.replace(/{{\s*(\w+)\s*}}/g, (match, p1) => data[p1] || '');
    },
    async fetchEmailTemplate() {
      try {
        const template = await this.EmailService.getTemplateByKey('invoice_email_temp');
        this.defaultSubject = this.replacePlaceholders(template.subject || '', {
          invoiceNumber: this.invoiceNumber
        });
        this.defaultEmailText = this.replacePlaceholders(template.body || '', {
          customer: `${this.person.firstname || ''} ${this.person.lastname || ''}`,
          invoiceNumber: this.invoiceNumber,
          dueDate: this.invoice.payment_due_date || '',
          invoiceAmount: this.formatMoney(this.invoice.total_value)
        });

        this.emailSubject = this.defaultSubject;
        this.emailMessage = this.defaultEmailText;

      } catch (error) {
        console.error('Error fetching email template:', error);
      }
    },
    setAsDefault() {
      this.emailSubject = this.defaultSubject;
      this.emailMessage = this.defaultEmailText;
    },
    async sendInvoice() {
      if (!this.recipientEmail || !this.recipientEmail.trim()) {
        this.notifyWarning('Please enter the recipient email address.');
        return;
      }

      const emailData = {
        template_key: 'invoice_email_temp', // Key for the invoice email template
        reference_id: this.invoiceId,
        to: [
          {
            name: `${this.person.firstname || 'Hi'} ${this.person.lastname || ''}`,
            email: this.recipientEmail.trim()
          }
        ],
        cc: this.ccEmail ? this.ccEmail : [],
        bcc: [],
        status: "PENDING",
        category: "notification",
        data: {
          customer: this.customer.name || '',
          invoiceNumber: this.invoiceNumber,
          dueDate: this.invoice.payment_due_date || '',
          invoiceAmount: this.formatMoney(this.invoice.total_value),
        },
        use_custom_subject_body: true,
        custom_subject: this.emailSubject,
        custom_body: this.emailMessage,
        has_attachments: this.hasAttachments,
        attachments: this.hasAttachments ? [
          {
            url: `invoices/${this.invoiceId}/report/invoice`,
            file_name: `${this.invoiceNumber}-invoice.pdf`
          }
        ] : []
      };

      try {
        await this.EmailService.createQueueItem(emailData);
        this.notifySuccess('Invoice email sent successfully', 'success');
        await this.$refs.emailQueueTable.loadEmailQueues();
      } catch (error) {
        console.error('Error sending invoice email:', error);
        this.notifyError('Failed to send invoice email', 'error');
      }
    }
  },
};
</script>

<style scoped>
/* No additional styles required */
</style>
